/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, { useState } from 'react';
import { IconButton } from '@mui/material';
import ArgosFlexbox from '../../../../../../../components/Containers/ArgosFlexbox';
import { EDatasetExecutionStatus } from '../../../../constants';
import { selectRedlineDatasetIdsToData, setTriggerFetchDatasetsData } from '../../../../../../../../redux/ducks/CoverageRequirements/slice';
import { useAppDispatch, useAppSelector } from '../../../../../../../../redux/hooks';
import _ from 'lodash';
import { ITreeNode } from '../../LeftPanelComponents/TreeComponent/CoverageRequirementsTreeComponent';
import { usePostRequest } from '../../../../../../../../api/helper';
import { CAPABILITY_TYPE_STR, datasetSaveData, OBJECT_TYPE_STR, PACKAGE_TYPE_STR, ROAD_TYPE_TYPE_STR, USE_CASE_TYPE_STR } from '../../RightPanelComponents/constants';
import toast from 'react-hot-toast';
import { WarningDialog } from './BulkEditWarningDialog';
import BulkEditField from './BulkEditField';
import { DrivingScenarioProperty } from '../../../ExecutionStatusTab/constants';
import { Spinner } from 'react-bootstrap';
import { MultiPageWizard } from '../../../../../../../components/MultiPageWizard/MultiPageWizard';
import ArgosDialogHeader from '../../../../../../../components/Dialogs/ArgosDialogHeader';
import { dialogHeight, Header } from '../constants';
import CloseIcon from '@mui/icons-material/Close';
import {validateURL} from "../../../../../../../../helpers/url/urlHelpers";

interface Props {
    onCloseDialog: any;
    rawCheckedNodes: ITreeNode[]; // selected datasets
    studyId: string;
    fetchStudy: Function;
}

/**
 * Popup dialog for bulk editing dataset configurations in Coverage Requirements App.
 */
const BulkEditDialog: React.FC<Props> = (props) => {
    const dispatch = useAppDispatch()
    const [isWarningDialogOpen, setWarningDialogOpen] = useState<boolean>(false)
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState<boolean>(true)
    const [isUpdating, setIsUpdating] = useState<boolean>(false)
    const datasetNodes = props.rawCheckedNodes.filter((node) => node.nodeType == "dataset")

    const [checkedProperties, setCheckedProperties] = useState<string[]>([])
    const onPropertyChecked = (property: any) => {
        var newCheckedProperties = checkedProperties
        if (checkedProperties.includes(property)) {
            newCheckedProperties = checkedProperties.filter((p) => p !== property)
        } else {
            newCheckedProperties = [...checkedProperties, property]
        }
        setCheckedProperties(newCheckedProperties)
        setIsUpdateButtonDisabled(!canUpdateProperties(configState, newCheckedProperties))
    }

    // Get Unique Properties List
    const redlineDatasetIdsToData = useAppSelector(selectRedlineDatasetIdsToData)
    var properties: DrivingScenarioProperty[] = []
    if (redlineDatasetIdsToData && Object.values(redlineDatasetIdsToData)[0]) {
        properties = _.uniqBy(Object.values(redlineDatasetIdsToData)[0].driving_scenario_properties, 'type')
    }

    const [configState, setConfigState] = useState<any>({})
    const updateConfigState = (state: any) => {
        var newState = configState
        newState[state.type] = state
        setConfigState(newState);
        setIsUpdateButtonDisabled(!canUpdateProperties(newState, checkedProperties))
    }

    /**
     * Helper Functions
     */
    // Handle saving to multiple datasets and passing changes to database
    const fetchSaveDataHandle = usePostRequest(datasetSaveData)

    /**
     * Formats dataset properties data into payload format.
     * @returns payloadToSend: payload to send to the database
     */
    const returnDataForPayload = () => {
        var formattedConfigState: any[] = []
        if (checkedProperties.includes(OBJECT_TYPE_STR) && configState[OBJECT_TYPE_STR] &&
            configState[OBJECT_TYPE_STR].length > 0) {
            configState[OBJECT_TYPE_STR].forEach((object: any) => {
                formattedConfigState.push({
                    type: OBJECT_TYPE_STR,
                    name: object.name,
                    category: object.category,
                    property_link: object.property_link,
                    objectId: object.objectId
                })
            })
        }
        if (checkedProperties.includes(ROAD_TYPE_TYPE_STR) && configState[ROAD_TYPE_TYPE_STR] &&
            configState[ROAD_TYPE_TYPE_STR].length > 0) {
            configState[ROAD_TYPE_TYPE_STR].forEach((object: any) => {
                formattedConfigState.push({
                    type: ROAD_TYPE_TYPE_STR,
                    name: object.name,
                    category: object.category,
                    property_link: object.property_link,
                })
            })
        }
        checkedProperties.forEach((key) => {
            if (key != OBJECT_TYPE_STR && key != ROAD_TYPE_TYPE_STR && configState[key]) {
                var fields: any = {
                    type: key,
                    name: configState[key].name,
                    category: configState[key].category ?? "",
                    property_link: configState[key].property_link ?? ""
                }
                if (key == PACKAGE_TYPE_STR) {
                    fields["metaData"] = configState[key].metaData ?? {}
                }
                formattedConfigState.push(fields)
            }
        })
        const payloadToSend: any = {
            "driving_scenario_properties": formattedConfigState
        };
        return payloadToSend
    }

    /**
     * Saves dataset properties data to the database.
     */
    const saveDataToDatabase = () => {
        setIsUpdating(true)
        var requests: any = []
        datasetNodes.forEach((node) => {
            let payload: any = {
                data: returnDataForPayload(),
                datasetId: node.datasetId,
                studyId: props?.studyId
            }
            if (node.executionStatus == EDatasetExecutionStatus.COMPLETED) {
                payload["executionStatus"] = EDatasetExecutionStatus.INVALID_RESULTS
            }
            requests.push(fetchSaveDataHandle(payload))
        })
        Promise.all(requests).then(() => {
            props.fetchStudy()
            dispatch(setTriggerFetchDatasetsData())
            props.onCloseDialog()
        }).catch(() => {
            toast.error("Failed to update dataset configurations")
        }).finally(() => {
            setIsUpdating(false)
            toast.success("Successfully updated dataset configurations")
        })
    }

    const selectionPage = !properties ? <Spinner /> :(
            <ArgosFlexbox
                style={{
                    overflow: "auto", maxHeight: dialogHeight, overflowX: "hidden", flexDirection: "column",
                    justifyContent: 'space-between', marginLeft: 20, marginRight: 10
                }}>
                { properties.map((property: { type: string; }) =>
                        <BulkEditField
                            key={property.type}
                            name={property.type}
                            property={property}
                            configState={configState} setConfigState={updateConfigState}
                            propertySelected={onPropertyChecked}
                        />)
                }
            </ArgosFlexbox>)

    return (
        <ArgosFlexbox vertical style={{overflow:"hidden"}}>
            <ArgosDialogHeader>
                <ArgosFlexbox style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Header>
                        <div style={{
                            color: 'white',
                            paddingLeft: 5,
                            fontSize: 14
                        }}>{"Update Configurations for Selected Datasets"}</div>
                    </Header>
                    <IconButton onClick={props?.onCloseDialog} sx={{ color: 'white', padding: 0, fontSize: 'small' }}>
                        <CloseIcon />
                    </IconButton>
                </ArgosFlexbox>
            </ArgosDialogHeader>
            <MultiPageWizard
                onClickClose={props.onCloseDialog}
                onFinish={saveDataToDatabase}
                isFinishLoading={isUpdating}
                isNextDisabled={[isUpdateButtonDisabled]}
                finishButtonText='Update'
                steps={["Select Configurations", "Confirm Update"]}
                children={[
                    selectionPage,
                    (<WarningDialog
                        rawCheckedNodes={datasetNodes}
                        onCloseDialog={() => setWarningDialogOpen(false)}
                        onConfirmUpdate={saveDataToDatabase}
                        isUpdating={isUpdating}
                    />)]
                }
            />
        </ArgosFlexbox>
    );
};

/**
 * Checks if the properties are valid for sending a bulk update request to the database.
 * User must check at least 1 property to bulk edit, and checked properties must have some filled out value.
 * Objects List must have at least 1 object if Objects is checked.
 * 
 * @param {any} configState - The current state of the configuration.
 * @param {string[]} checkedProperties - The properties that are checked for bulk update.
 * 
 * @returns {boolean} - Returns `true` if can send a bulk update request to the database, `false` otherwise.
 */
export const canUpdateProperties = (configState: any, checkedProperties: string[]): boolean => {
    return checkedProperties.length > 0 && checkedProperties.every((property: string) => {
        if (configState[property]) {
            switch (property) {
                case OBJECT_TYPE_STR:
                    return configState[property].length > 0; // At least one object was selected
                case ROAD_TYPE_TYPE_STR:
                    return configState[property].length > 0; // At least one road type was selected
                case PACKAGE_TYPE_STR:
                    return configState[property].metaData.modelYear && configState[property].name
                case USE_CASE_TYPE_STR:
                case CAPABILITY_TYPE_STR:
                    return configState[property].name && configState[property].property_link &&
                        validateURL(configState[property].property_link)
                case "undefined":
                    return true
                default:
                    return configState[property].name
            }
        }
        return false
    });
}

export default BulkEditDialog;